import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Sparkles } from "lucide-react";

const NotFoundPage = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Page not found · The Story Assembly";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div
      data-testid="not-found-page"
      className="min-h-screen bg-[#F4F1EA] text-[#1C1A17] flex flex-col"
    >
      <header className="border-b border-[#DCD7C9]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center">
          <Link to="/" data-testid="nf-logo" className="flex items-center gap-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
              alt="The Story Assembly"
              className="h-9 w-9 rounded-md object-cover ring-1 ring-[#1C1A17]/10"
            />
            <span className="font-serif text-xl tracking-tight">
              The <span className="italic font-light">Story</span>{" "}
              <span className="text-[#D4A53A]">Assembly</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#D4A53A]">
            404 · Page not found
          </div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter mt-4 leading-[1.02] text-[#0A1E3F]">
            Even the best stories{" "}
            <span className="italic font-light">have plot holes.</span>
          </h1>
          <p className="mt-6 text-[17px] leading-relaxed text-[#1C1A17]/75 max-w-2xl">
            The page you were after doesn't exist — or it never did. While you're here,
            try one of these instead. They're better than scrolling back.
          </p>

          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            <Link
              to="/"
              data-testid="nf-cta-demo"
              className="group block rounded-md border border-[#DCD7C9] p-5 bg-white hover:border-[#D4A53A] transition-colors"
            >
              <Sparkles size={16} className="text-[#D4A53A]" />
              <div className="font-serif text-[20px] text-[#0A1E3F] mt-3 leading-snug group-hover:text-[#D4A53A] transition-colors">
                Try the demo
              </div>
              <p className="mt-2 text-[13px] text-[#1C1A17]/65">
                Generate a brand story in 60 seconds. Three goes a day, free.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-mono-display uppercase tracking-[0.18em] text-[#0A1E3F]">
                Open <ArrowRight size={11} />
              </span>
            </Link>
            <Link
              to="/blog"
              data-testid="nf-cta-blog"
              className="group block rounded-md border border-[#DCD7C9] p-5 bg-white hover:border-[#D4A53A] transition-colors"
            >
              <BookOpen size={16} className="text-[#D4A53A]" />
              <div className="font-serif text-[20px] text-[#0A1E3F] mt-3 leading-snug group-hover:text-[#D4A53A] transition-colors">
                Read the Journal
              </div>
              <p className="mt-2 text-[13px] text-[#1C1A17]/65">
                Five essays on tagline writing, positioning and brand voice.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-mono-display uppercase tracking-[0.18em] text-[#0A1E3F]">
                Browse <ArrowRight size={11} />
              </span>
            </Link>
            <Link
              to="/examples/nike"
              data-testid="nf-cta-nike"
              className="group block rounded-md border border-[#DCD7C9] p-5 bg-white hover:border-[#D4A53A] transition-colors"
            >
              <ArrowUpRight size={16} className="text-[#D4A53A]" />
              <div className="font-serif text-[20px] text-[#0A1E3F] mt-3 leading-snug group-hover:text-[#D4A53A] transition-colors">
                Nike Brand Story
              </div>
              <p className="mt-2 text-[13px] text-[#1C1A17]/65">
                Our deep dive into how Nike built a brand people believe in.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-mono-display uppercase tracking-[0.18em] text-[#0A1E3F]">
                Read <ArrowRight size={11} />
              </span>
            </Link>
          </div>

          <div className="mt-14 pt-8 border-t border-[#DCD7C9] font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
            Or head back to the{" "}
            <Link
              to="/"
              data-testid="nf-home-link"
              className="text-[#0A1E3F] hover:text-[#D4A53A] underline-offset-4 underline transition-colors"
            >
              home page
            </Link>
            .
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotFoundPage;
